"use client"

import { useState, useEffect } from "react"
import { useRouter } from "next/navigation"
import { CollectionCard } from "./collection-card"
import { Button } from "@/components/ui/button"
import { ArrowRight, Sparkles, Star } from "lucide-react"

interface Collection {
  id: string
  title: string
  description: string
  tagline?: string
  category: string
  isFeatured: boolean
  visitCount: number
  books: Array<{
    book: {
      id: string
      title: string
      author: string
      coverUrl?: string
    }
  }>
  _count: {
    books: number
  }
}

export function FeaturedCollections() {
  const [collections, setCollections] = useState<Collection[]>([])
  const [loading, setLoading] = useState(true)
  const router = useRouter()

  useEffect(() => {
    const fetchCollections = async () => {
      try {
        const response = await fetch('/api/collections?featured=true&limit=4')
        if (response.ok) {
          const data = await response.json()
          setCollections(data.collections)
        }
      } catch (error) {
        console.error('Error fetching collections:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchCollections()
  }, [])

  return (
    <section className="relative py-12 sm:py-20 overflow-hidden">
      {/* Animated Background with Gradients */}
      <div className="absolute inset-0 bg-gradient-to-br from-violet-50 via-purple-50 to-fuchsia-50 dark:from-violet-950/20 dark:via-purple-950/20 dark:to-fuchsia-950/20"></div>
      
      {/* Animated Orbs */}
      <div className="absolute top-0 left-0 w-72 sm:w-96 h-72 sm:h-96 bg-gradient-to-br from-purple-400/30 to-pink-400/30 dark:from-purple-600/20 dark:to-pink-600/20 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute bottom-0 right-0 w-72 sm:w-96 h-72 sm:h-96 bg-gradient-to-tl from-blue-400/30 to-cyan-400/30 dark:from-blue-600/20 dark:to-cyan-600/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-72 sm:w-96 h-72 sm:h-96 bg-gradient-to-r from-violet-400/20 to-purple-400/20 dark:from-violet-600/10 dark:to-purple-600/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
      
      {/* Decorative Pattern Overlay */}
      <div className="absolute inset-0 opacity-[0.03] dark:opacity-[0.02]" style={{
        backgroundImage: 'radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)',
        backgroundSize: '40px 40px'
      }}></div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Header with Floating Animation */}
        <div className="text-center mb-10 sm:mb-16">
          <div className="inline-flex items-center justify-center gap-2 sm:gap-3 mb-4 px-4 sm:px-6 py-2.5 sm:py-3 bg-gradient-to-r from-purple-500/10 to-pink-500/10 dark:from-purple-500/20 dark:to-pink-500/20 rounded-full border border-purple-200/50 dark:border-purple-500/30 backdrop-blur-sm">
            <Sparkles className="h-6 w-6 text-purple-600 dark:text-purple-400 animate-pulse" />
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold bg-gradient-to-r from-purple-600 via-pink-600 to-violet-600 dark:from-purple-400 dark:via-pink-400 dark:to-violet-400 bg-clip-text text-transparent">
              Featured Collections
            </h2>
            <Star className="h-6 w-6 text-pink-600 dark:text-pink-400 animate-pulse" style={{ animationDelay: '0.5s' }} />
          </div>
          <p className="text-muted-foreground text-sm sm:text-lg md:text-xl max-w-3xl mx-auto leading-relaxed">
            Discover carefully curated book collections designed to{' '}
            <span className="font-semibold text-purple-600 dark:text-purple-400">transform your thinking</span>
            {' '}and{' '}
            <span className="font-semibold text-pink-600 dark:text-pink-400">accelerate your growth</span>
          </p>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 min-[480px]:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
            {[...Array(4)].map((_, i) => (
              <div 
                key={i} 
                className="h-80 bg-gradient-to-br from-white/60 to-white/40 dark:from-slate-800/60 dark:to-slate-800/40 backdrop-blur-sm animate-pulse rounded-xl border border-white/50 dark:border-slate-700/50 shadow-lg"
              />
            ))}
          </div>
        ) : collections.length > 0 ? (
          <>
            <div className="grid grid-cols-1 min-[480px]:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-8 sm:mb-12">
              {collections.map((collection, index) => (
                <div
                  key={collection.id}
                  className="animate-fadeInUp"
                  style={{ animationDelay: `${index * 0.1}s`, animationFillMode: 'both' }}
                >
                  <CollectionCard collection={collection} />
                </div>
              ))}
            </div>
            <div className="text-center">
              <Button 
                onClick={() => router.push('/collections')}
                size="lg"
                className="group bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white shadow-lg hover:shadow-xl transition-all duration-300 px-8 py-6 text-lg"
              >
                Explore All Collections
                <ArrowRight className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
          </>
        ) : (
          <div className="text-center py-16">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-purple-100 to-pink-100 dark:from-purple-900/30 dark:to-pink-900/30 rounded-full mb-6">
              <Sparkles className="h-10 w-10 text-purple-600 dark:text-purple-400" />
            </div>
            <p className="text-muted-foreground text-lg">No featured collections available at the moment.</p>
          </div>
        )}
      </div>

      {/* Add fadeInUp animation */}
      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeInUp {
          animation: fadeInUp 0.6s ease-out;
        }
      `}</style>
    </section>
  )
}
